
import { type PropertyCard as PropertyCardData } from '@resolute/schema';
import { Button } from '@resolute/ui';
import { SearchX } from 'lucide-react';

import { Reveal } from '@/components/motion/reveal';
import { PropertyCard } from '@/components/property/property-card';
import { PropertyCardSkeleton } from '@/components/property/property-card-skeleton';

interface ResultsGridProps {
  items?: PropertyCardData[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  onRetry: () => void;
  onClearFilters: () => void;
}

export function ResultsGrid({
  items,
  isLoading,
  isError,
  isFetching,
  onRetry,
  onClearFilters,
}: ResultsGridProps) {
  if (isError) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-4 rounded-lg border border-border bg-card p-12 text-center"
      >
        <p className="text-muted-foreground">Something went wrong loading results.</p>
        <p className="text-sm text-muted-foreground">
          This can happen if a filter (like a price range) ended up invalid.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClearFilters}>
            Clear filters
          </Button>
          <Button onClick={onRetry}>Try again</Button>
        </div>
      </div>
    );
  }

  if (isLoading || !items) {
    return (
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <PropertyCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 rounded-lg border border-dashed border-border p-12 text-center">
        <SearchX className="size-10 text-muted-foreground" aria-hidden="true" />
        <div>
          <p className="font-medium">No properties match your filters</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Try widening your budget or removing a filter.
          </p>
        </div>
        <Button variant="outline" onClick={onClearFilters}>
          Clear filters
        </Button>
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-1 gap-6 transition-opacity sm:grid-cols-2 xl:grid-cols-3"
      style={{ opacity: isFetching ? 0.6 : 1 }}
      aria-busy={isFetching}
    >
      {/* Paginated result count varies and can span multiple screens, so each
          card reveals independently on its own scroll entry rather than a
          single group-level stagger (which would resolve before the lower
          rows are ever actually scrolled to). */}
      {items.map((property) => (
        <Reveal key={property.id}>
          <PropertyCard property={property} />
        </Reveal>
      ))}
    </div>
  );
}
